<template>
  <div class="p-6 md:p-10 flex flex-col gap-6 max-w-7xl mx-auto w-full dark:text-white">
    <!-- Header -->
    <div class="flex flex-wrap items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4 gap-4">
      <div>
        <h1 class="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Test View & Laboratório</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Ambiente de testes para o novo componente MusicSheet, Extensão TipTap e Dialogs/Toasts.</p>
      </div>

      <!-- Navigation Tabs -->
      <div class="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 p-1.5 rounded-xl">
        <button
          @click="activeTab = 'musicsheet'"
          :class="activeTab === 'musicsheet' ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'"
          class="px-4 py-2 text-sm font-semibold rounded-lg transition-all"
        >
          🎵 MusicSheet (HTML/Vue)
        </button>
        <button
          @click="activeTab = 'tiptap'"
          :class="activeTab === 'tiptap' ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'"
          class="px-4 py-2 text-sm font-semibold rounded-lg transition-all"
        >
          📝 TipTap + MusicSheet
        </button>
        <button
          @click="activeTab = 'system'"
          :class="activeTab === 'system' ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'"
          class="px-4 py-2 text-sm font-semibold rounded-lg transition-all"
        >
          ⚙️ Toast & Dialogs
        </button>
      </div>
    </div>

    <!-- TAB 1: Standalone MusicSheet Component -->
    <div v-if="activeTab === 'musicsheet'" class="flex flex-col gap-6">
      <div class="bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4 text-sm text-indigo-900 dark:text-indigo-200 flex items-start gap-3">
        <div class="p-2 bg-indigo-600 text-white rounded-lg mt-0.5">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
        </div>
        <div>
          <h4 class="font-bold">Edição 100% Visual com o Mouse:</h4>
          <p class="mt-0.5 text-xs opacity-90">
            • Mova o mouse sobre a pauta para ver a prévia da nota (linha/espaço).<br/>
            • Clique para inserir a nota na duração e alteração selecionada (com som imediato).<br/>
            • Clique com o botão direito sobre uma nota para removê-la.<br/>
            • Use os botões <strong>Importar</strong> / <strong>Exportar</strong> para ler ou gravar em <strong>ABC Notation</strong> ou <strong>MusicXML</strong>.<br/>
            • Clique em <strong>Ouvir</strong> para reproduzir a música com sintetizador e cursor animado.
          </p>
        </div>
      </div>

      <!-- Quick Sample Loaders -->
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Carregar Melodias de Teste:</span>
        <button
          @click="loadPreset('repeatSample')"
          class="px-3 py-1.5 bg-amber-50 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700 text-xs font-semibold text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-800 shadow-sm transition"
        >
          🔁 Repetições (Ritornelo & 1ª/2ª Vez)
        </button>
        <button
          @click="loadPreset('amazingGrace')"
          class="px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/40 border border-emerald-300 dark:border-emerald-700 text-xs font-semibold text-emerald-700 dark:text-emerald-300 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-800 shadow-sm transition"
        >
          🎹 Amazing Grace (Piano - 2 Mãos)
        </button>
        <button
          @click="loadPreset('zocharti')"
          class="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-300 dark:border-indigo-700 text-xs font-semibold text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-800 shadow-sm transition"
        >
          ✨ Zocharti Loch (4 Vozes - SATB)
        </button>
        <button
          @click="loadPreset('odeToJoy')"
          class="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm transition"
        >
          🎼 Ode à Alegria (Beethoven)
        </button>
        <button
          @click="loadPreset('scale')"
          class="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm transition"
        >
          🎹 Escala de Dó Maior (C Major Scale)
        </button>
        <button
          @click="loadPreset('twinkle')"
          class="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm transition"
        >
          ⭐ Brilha Brilha Estrelinha (Twinkle)
        </button>
        <button
          @click="clearStandalone"
          class="px-3 py-1.5 bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800 text-xs font-medium rounded-lg hover:bg-red-100 shadow-sm transition ml-auto"
        >
          🗑️ Pauta em Branco
        </button>
      </div>

      <!-- Standalone MusicSheet Instance -->
      <MusicSheet
        ref="standaloneMusicSheet"
        v-model="standaloneAbc"
        :title="standaloneTitle"
        time-signature="4/4"
        key-signature="C"
        clef="treble"
        @change="handleStandaloneChange"
      />

      <!-- Realtime Output Inspector -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="bg-gray-900 text-gray-100 rounded-xl p-4 border border-gray-800 flex flex-col gap-2">
          <div class="flex items-center justify-between border-b border-gray-800 pb-2">
            <span class="text-xs font-bold text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
              <span class="w-2 h-2 rounded-full bg-emerald-400"></span>
              Saída ABC Notation (Live):
            </span>
          </div>
          <pre class="text-xs font-mono text-emerald-400 overflow-x-auto p-2 bg-black/40 rounded-lg max-h-48">{{ latestExportData.abc || standaloneAbc }}</pre>
        </div>

        <div class="bg-gray-900 text-gray-100 rounded-xl p-4 border border-gray-800 flex flex-col gap-2">
          <div class="flex items-center justify-between border-b border-gray-800 pb-2">
            <span class="text-xs font-bold text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
              <span class="w-2 h-2 rounded-full bg-indigo-400"></span>
              Saída MusicXML (Live preview):
            </span>
          </div>
          <pre class="text-xs font-mono text-indigo-300 overflow-x-auto p-2 bg-black/40 rounded-lg max-h-48">{{ latestExportData.musicXml || 'Modifique a partitura para gerar MusicXML...' }}</pre>
        </div>
      </div>
    </div>

    <!-- TAB 2: TipTap + MusicSheet Extension -->
    <div v-if="activeTab === 'tiptap'" class="flex flex-col gap-6">
      <div class="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl p-4 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-3">
        <div class="p-2 bg-amber-600 text-white rounded-lg mt-0.5">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
        </div>
        <div>
          <h4 class="font-bold">Extensão TipTap: MusicSheet Node</h4>
          <p class="mt-0.5 text-xs opacity-90">
            Abaixo temos o editor TipTap com o nó <code>MusicSheetExtension</code> integrado. Você pode misturar texto rico, títulos, parágrafos e blocos de partitura que podem ser editados diretamente pelo mouse dentro do próprio documento e salvos em HTML puro!
          </p>
        </div>
      </div>

      <!-- TipTap Custom Editor Container -->
      <div class="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden flex flex-col">
        <!-- Editor Toolbar -->
        <div v-if="tiptapEditor" class="p-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-1.5 text-xs">
          <button
            @click="tiptapEditor.chain().focus().toggleBold().run()"
            :class="{ 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300': tiptapEditor.isActive('bold') }"
            class="px-2.5 py-1.5 font-bold rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            B
          </button>
          <button
            @click="tiptapEditor.chain().focus().toggleItalic().run()"
            :class="{ 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300': tiptapEditor.isActive('italic') }"
            class="px-2.5 py-1.5 italic font-serif rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            I
          </button>
          <button
            @click="tiptapEditor.chain().focus().toggleHeading({ level: 2 }).run()"
            :class="{ 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300': tiptapEditor.isActive('heading', { level: 2 }) }"
            class="px-2.5 py-1.5 font-semibold rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            H2
          </button>
          <button
            @click="tiptapEditor.chain().focus().toggleHeading({ level: 3 }).run()"
            :class="{ 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300': tiptapEditor.isActive('heading', { level: 3 }) }"
            class="px-2.5 py-1.5 font-semibold rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            H3
          </button>

          <div class="h-5 w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>

          <!-- Insert MusicSheet Command -->
          <button
            @click="insertMusicSheetIntoTipTap"
            class="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg shadow-sm transition"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path></svg>
            <span>+ Inserir Partitura Musical</span>
          </button>
        </div>

        <!-- Editor Content Area -->
        <div class="p-6 min-h-[300px] prose dark:prose-invert max-w-none focus:outline-none">
          <editor-content :editor="tiptapEditor" />
        </div>
      </div>

      <!-- TipTap HTML Output Preview -->
      <div class="bg-gray-900 text-gray-100 rounded-xl p-4 border border-gray-800 flex flex-col gap-2">
        <div class="flex items-center justify-between border-b border-gray-800 pb-2">
          <span class="text-xs font-bold text-gray-300 uppercase tracking-wider">
            HTML Gerado pelo TipTap com Partitura Embutida:
          </span>
          <button @click="copyTipTapHtml" class="text-xs text-indigo-400 hover:underline">Copiar HTML</button>
        </div>
        <pre class="text-xs font-mono text-emerald-400 overflow-x-auto p-2 bg-black/40 rounded-lg max-h-40">{{ tiptapHtml }}</pre>
      </div>
    </div>

    <!-- TAB 3: System Tests (Toasts & Dialogs) -->
    <div v-if="activeTab === 'system'" class="flex flex-col gap-6">
      <div class="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col gap-4">
        <h2 class="text-lg font-bold text-gray-900 dark:text-white">Toast Component Tests</h2>
        <div class="flex flex-wrap gap-2">
          <button @click="showToast('success')" class="px-4 py-2 bg-green-500 text-white rounded-lg shadow hover:bg-green-600 transition">Toast Success</button>
          <button @click="showToast('error')" class="px-4 py-2 bg-red-500 text-white rounded-lg shadow hover:bg-red-600 transition">Toast Error</button>
          <button @click="showToast('info')" class="px-4 py-2 bg-blue-500 text-white rounded-lg shadow hover:bg-blue-600 transition">Toast Info</button>
          <button @click="showToast('warning')" class="px-4 py-2 bg-yellow-500 text-white rounded-lg shadow hover:bg-yellow-600 transition">Toast Warning</button>
          <button @click="showToast(null)" class="px-4 py-2 bg-gray-500 text-white rounded-lg shadow hover:bg-gray-600 transition">Toast Default</button>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col gap-4">
        <h2 class="text-lg font-bold text-gray-900 dark:text-white">Dialog Component Tests</h2>
        <div class="flex flex-wrap gap-2">
          <button @click="testAlert" class="px-4 py-2 bg-indigo-500 text-white rounded-lg shadow hover:bg-indigo-600 transition">Test Alert</button>
          <button @click="testConfirm" class="px-4 py-2 bg-rose-500 text-white rounded-lg shadow hover:bg-rose-600 transition">Test Confirm</button>
          <button @click="testPrompt" class="px-4 py-2 bg-emerald-500 text-white rounded-lg shadow hover:bg-emerald-600 transition">Test Prompt</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import MusicSheet from '@/components/common/MusicSheet.vue';
import MusicSheetExtension from '@/extensions/MusicSheetExtension';
import toastStore from '@/store/toast.store';
import dialogStore from '@/store/dialog.store';

const activeTab = ref('musicsheet');

// Standalone MusicSheet State
const standaloneMusicSheet = ref(null);
const standaloneTitle = ref('Melodia Teste');
const standaloneAbc = ref('');
const latestExportData = ref({
  abc: '',
  musicXml: '',
  notes: []
});

function handleStandaloneChange(data) {
  latestExportData.value = data;
}

function clearStandalone() {
  if (standaloneMusicSheet.value) {
    standaloneMusicSheet.value.clearScore();
  }
}

function loadPreset(presetName) {
  if (presetName === 'repeatSample') {
    standaloneTitle.value = 'Minueto (Exemplo com Repetição & Voltas)';
    standaloneAbc.value = `X:1
T:Minueto com Repetição e Voltas
C:Tradicional
M:4/4
L:1/8
Q:1/4=110
K:C
|: C4 D4 E4 F4 | G4 A4 B4 c4 |1 c4 B4 A4 G4 :|2 F4 E4 D4 C8 ||`;
  } else if (presetName === 'amazingGrace') {
    standaloneTitle.value = 'Amazing Grace (Piano)';
    standaloneAbc.value = `X:1
T:Amazing Grace
C:Traditional (arr. Piano)
M:3/4
L:1/8
Q:1/4=72
%%score RH LH
V:RH clef=treble name="Mão Direita"
V:LH clef=bass name="Mão Esquerda"
K:G
[V:RH] D2 | G4 (B2 | B4) A2 | G4 E2 | D4 D2 | G4 (B2 | B4) d2 | d6- | d4
[V:LH] z2 | [G,4B,4D4] z2 | [G,4B,4D4] z2 | [C4E4G4] z2 | [G,4B,4D4] z2 | [G,4B,4D4] z2 | [G,4B,4D4] z2 | [D4F4A4] z2 | [D4F4A4]`;
  } else if (presetName === 'zocharti') {
    standaloneTitle.value = 'Zocharti Loch (Louis Lewandowski)';
    standaloneAbc.value = `X:1
T:Zocharti Loch
C:Louis Lewandowski
M:C
L:1/8
Q:1/4=76
%%score (T1 T2) (B1 B2)
V:T1 clef=treble name="Tenore I"
V:T2 clef=treble name="Tenore II"
V:B1 clef=bass name="Basso I"
V:B2 clef=bass name="Basso II"
K:Gm
[V:T1] (B2c2 d2g2) | f6e2 |
[V:T2] (G2A2 B2e2) | d6c2 |
[V:B1] z8 | z2f2 g2a2 |
[V:B2] x8 | x8 |`;
  } else if (presetName === 'odeToJoy') {
    standaloneTitle.value = 'Ode à Alegria (Beethoven)';
    standaloneAbc.value = `X:1\nT:Ode à Alegria (Beethoven)\nM:4/4\nL:1/8\nQ:1/4=120\nK:C\nE4 E4 F4 G4 | G4 F4 E4 D4 | C4 C4 D4 E4 | E6 D2 D8 |\nE4 E4 F4 G4 | G4 F4 E4 D4 | C4 C4 D4 E4 | D6 C2 C8 |`;
  } else if (presetName === 'scale') {
    standaloneTitle.value = 'Escala de Dó Maior';
    standaloneAbc.value = `X:1\nT:Escala de Dó Maior\nM:4/4\nL:1/8\nQ:1/4=100\nK:C\nC4 D4 E4 F4 | G4 A4 B4 c4 | c4 B4 A4 G4 | F4 E4 D4 C8 |`;
  } else if (presetName === 'twinkle') {
    standaloneTitle.value = 'Brilha Brilha Estrelinha';
    standaloneAbc.value = `X:1\nT:Brilha Brilha Estrelinha\nM:4/4\nL:1/8\nQ:1/4=90\nK:C\nC4 C4 G4 G4 | A4 A4 G8 | F4 F4 E4 E4 | D4 D4 C8 |`;
  }
}

// TipTap Editor with MusicSheet Extension Setup
const tiptapHtml = ref('');

const tiptapEditor = useEditor({
  extensions: [
    StarterKit,
    MusicSheetExtension,
  ],
  content: `
    <h2>Documento de Arranjo Musical com TipTap</h2>
    <p>Este documento demonstra a integração da partitura interativa diretamente no fluxo de texto.</p>
    <p>Abaixo está um exemplo de partitura embutida como bloco visual com tempo definido:</p>
    <div data-type="music-sheet" data-title="Tema Principal" data-time-signature="4/4" data-key="C" data-tempo="120" data-clef="treble" data-abc="X:1&#10;T:Tema Principal&#10;M:4/4&#10;L:1/8&#10;Q:1/4=120&#10;K:C&#10;C4 E4 G4 c4 | B4 G4 E4 C8 |"></div>
    <p>Você pode alterar o andamento (BPM), adicionar mais notas com o mouse ou inserir novas partituras no documento através da barra de ferramentas acima.</p>
  `,
  onUpdate: ({ editor }) => {
    tiptapHtml.value = editor.getHTML();
  },
});

function insertMusicSheetIntoTipTap() {
  if (tiptapEditor.value) {
    tiptapEditor.value.chain().focus().insertMusicSheet({
      title: 'Nova Partitura',
      timeSignature: '4/4',
      key: 'C',
      clef: 'treble',
      abcData: 'X:1\nT:Nova Partitura\nM:4/4\nL:1/8\nK:C\nC4 D4 E4 F4 | G8 |'
    }).run();
  }
}

function copyTipTapHtml() {
  if (tiptapEditor.value) {
    navigator.clipboard.writeText(tiptapEditor.value.getHTML()).then(() => {
      showToast('success');
    });
  }
}

// Toast & Dialog Tests
const showToast = (type) => {
  toastStore.dispatch('addMessage', {
    message: `Este é um toast de teste do tipo: ${type || 'padrão'}.`,
    icon: type,
    ttl: 6000
  });
};

const testAlert = async () => {
  await dialogStore.dispatch('alert', {
    title: 'Aviso Importante',
    message: 'Esta é uma mensagem de alerta do sistema de diálogos.'
  });
  toastStore.dispatch('addMessage', { message: 'Alerta fechado com sucesso.', icon: 'info', ttl: 3000 });
};

const testConfirm = async () => {
  const result = await dialogStore.dispatch('confirm', {
    title: 'Confirmação de Exclusão',
    message: 'Você tem certeza que deseja prosseguir?'
  });
  if (result) {
    toastStore.dispatch('addMessage', { message: 'Ação confirmada!', icon: 'success', ttl: 3000 });
  } else {
    toastStore.dispatch('addMessage', { message: 'Ação cancelada.', icon: 'error', ttl: 3000 });
  }
};

const testPrompt = async () => {
  const result = await dialogStore.dispatch('prompt', {
    title: 'Nome da Partitura',
    message: 'Digite o título da sua nova partitura:',
    defaultValue: 'Minha Composição'
  });
  if (result !== null) {
    toastStore.dispatch('addMessage', { message: `Título definido: ${result}`, icon: 'success', ttl: 4000 });
  }
};

onMounted(() => {
  loadPreset('repeatSample');
  if (tiptapEditor.value) {
    tiptapHtml.value = tiptapEditor.value.getHTML();
  }
});

onBeforeUnmount(() => {
  if (tiptapEditor.value) {
    tiptapEditor.value.destroy();
  }
});
</script>